import React, { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Eye } from "lucide-react";
import api from "@/lib/api";

/**
 * Iter 45.69 — Google algorithm rollout banner (shared, dismissible).
 * Shown on SEO Performance and the main Dashboard whenever Google's official
 * status feed reports an update actively rolling out.
 *
 * - "Understood" dismisses the banner per browser per update (localStorage
 *   keyed by update name, so the NEXT update shows up again automatically).
 * - While dismissed, a small "rolling out" chip stays visible so the banner
 *   can be brought back with one click.
 * - Auto-disappears the moment the feed reports the rollout is complete
 *   (rolling_out flips false server-side) — no dismissal state lingers.
 */
export default function GoogleUpdateBanner() {
  const [googleUpdate, setGoogleUpdate] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    const fetchStatus = () =>
      api.get("/seo/google-update-status")
        .then((r) => { if (alive) setGoogleUpdate(r.data); })
        .catch(() => {});
    fetchStatus();
    const t = setInterval(fetchStatus, 15 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Re-read dismissal whenever a (new) update arrives — keyed per update
  // name so a completed rollout's dismissal never hides the next update.
  const updateName = googleUpdate?.update?.name || "";
  useEffect(() => {
    if (!updateName) return;
    try {
      setDismissed(localStorage.getItem(`googleUpdate.dismissed.${updateName}`) === "1");
    } catch {}
  }, [updateName]);

  if (!googleUpdate?.rolling_out || !googleUpdate.update) return null;
  const u = googleUpdate.update;

  const dismiss = () => {
    try { localStorage.setItem(`googleUpdate.dismissed.${updateName}`, "1"); } catch {}
    setDismissed(true);
  };
  const bringBack = () => {
    try { localStorage.removeItem(`googleUpdate.dismissed.${updateName}`); } catch {}
    setDismissed(false);
  };

  if (dismissed) {
    return (
      <button
        onClick={bringBack}
        className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 hover:border-amber-400 shadow-sm transition-all"
        data-testid="google-update-bring-back"
        title="Show the Google update banner again"
      >
        <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
        Google update rolling out
        <Eye className="h-3 w-3" />
      </button>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-xl border-2 border-amber-400 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 p-4 shadow-lg shadow-amber-500/15"
      data-testid="google-update-banner"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shrink-0 shadow-md animate-pulse">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-amber-900">Google update is rolling out guys!</span>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white" data-testid="google-update-type">
              {u.type}
            </span>
          </div>
          <div className="text-xs text-amber-800 mt-1" data-testid="google-update-name">
            <strong>{u.name}</strong>
            {u.started && (
              <>
                {" · started "}
                {new Date(u.started).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })}
              </>
            )}
          </div>
          <p className="text-xs text-amber-800 mt-1.5 leading-relaxed">
            Make sure to check Google Search Console to see if there is any traffic decline in your set of projects. And, take the necessary actions if PIP required.
          </p>
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <button
            onClick={dismiss}
            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-full px-3 py-1.5 shadow-sm transition-colors"
            data-testid="google-update-dismiss"
            title="Hide this banner (you can bring it back anytime)"
          >
            Understood 🫡
          </button>
          <a
            href={u.link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 border border-amber-300 bg-white/70 rounded-full px-3 py-1.5 transition-colors"
            data-testid="google-update-link"
          >
            Google status <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
